/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {ActionButton} from '@adobe/react-spectrum';
import BadgeIcon from './Badge/BadgeIcon';
import classNames from 'classnames';
import {getUnifiedShellUrl} from '@exc/url';
import metrics from '@adobe/exc-app/metrics';
import Popover from '@react/react-spectrum/Popover';
import React, {createContext, lazy, Suspense, useContext, useEffect, useRef, useState} from 'react';
import ShellOverlayTrigger from './ShellOverlayTrigger';
import ShellWait from './ShellWait';
import {useIntl} from 'react-intl';

const PulseWidgetWrapper = lazy(() => import('@exc/pulse-ui/lib/pulse/containers/PulseWidgetWrapper'));
const PulseContainer = lazy(() => import('@exc/pulse-ui/lib/pulse/containers/PulseContainer'));

const PulseContext = createContext();

/**
 * A required context provider that consumes 'pulse' object from Shell and provides
 * count updates and handlers to all shell pulse components.
 */
export class PulseDataProvider extends React.Component {
  constructor(props) {
    super(props);
    const {count = 0, hasNew = false} = props.pulse;
    this.state = {
      count,
      hasNew
    };
  }

  onPulseCountChanged(count, hasNew) {
    this.setState({count, hasNew});
  }

  render() {
    const pulse = this.props.pulse;
    const data = {
      modifiedCount: (pulse.additionalCount || 0) + this.state.count,
      onPulseCountChanged: this.onPulseCountChanged.bind(this),
      pulse,
      ...this.state
    };
    return (
      <PulseContext.Provider value={data}>
        {this.props.children}
      </PulseContext.Provider>
    );
  }
}

export function PulseBadge() {
  const {hasNew, modifiedCount} = useContext(PulseContext);
  return (
    <BadgeIcon
      count={modifiedCount}
      hasNewAnnouncement={hasNew}
      key="pulse-badge"
      maxDigits={1}
    />
  );
}

function PulsePopover(props) {
  const {environment, locale, open} = props;
  const onViewAll = (activeTab = '') => {
    const notificationUrl = getUnifiedShellUrl(environment, `/home/notifications/${activeTab}`);
    window.open(notificationUrl, '_self');
  };

  const fallback = (
    <Popover {...props} isDialog={false} role="dialog">
      <div style={{display: 'flex', justifyContent: 'center', top: '40px', width: '25rem'}}>
        <ShellWait locale={locale} size="M" />
      </div>
    </Popover>
  );

  if (!open) {
    return fallback;
  }

  return (
    <Suspense fallback={fallback}>
      <PulseWidgetWrapper onViewAllClicked={onViewAll} {...props} />
    </Suspense>
  );
}

class PulseBoundary extends React.Component {
  constructor() {
    super();
    this.metrics = metrics.create('exc.shell.Pulse');
    this.state = {isError: false};
  }

  static getDerivedStateFromError() {
    return {isError: true};
  }

  componentDidCatch(e) {
    this.metrics.error(e.toString());
  }

  render() {
    if (this.state.isError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const openPulseWithKeyDown = (e, pulseButtonRef) => {
  if (e.key === 'Enter' || e.key === ' ') {
    pulseButtonRef?.current?.UNSAFE_getDOMNode().click();
  }
};

const PulseButton = ({className, count, hasNew, modifiedCount, pulseButtonRef, tabIndex}) => {
  const {formatMessage} = useIntl();

  let label = formatMessage({
    defaultMessage: 'Notifications and Announcements',
    description: 'Label for the Notifications and Announcements Button',
    id: 'notification'});

  if (count) {
    label += `. ${formatMessage({
      defaultMessage: 'unread notifications',
      description: 'Label for unread notifications',
      id: 'unread'})} : ${modifiedCount} `;
  }

  if (hasNew) {
    label += `. ${formatMessage({
      defaultMessage: 'Has new announcement',
      description: 'New Announcement Label for Notification Button',
      id: 'newAnnouncement'})}`;
  }

  return (
    <ActionButton
      UNSAFE_className={classNames('pulse-menu', className)}
      aria-label={label}
      data-omega-element="pulse"
      isDisabled={tabIndex === -1}
      isQuiet
      onKeyDown={e => openPulseWithKeyDown(e, pulseButtonRef)} // Needed until pulse and the dialog are updated to v3
      ref={pulseButtonRef}
    >
      <PulseBadge />
    </ActionButton>
  );
};

export function PulseOverlayTrigger({closeOverlaysOnMenuShow, environment, tabIndex, theme, hasAccess}) {
  const [opened, setOpened] = useState(false);
  const {count, hasNew, modifiedCount, onPulseCountChanged, pulse} = useContext(PulseContext);
  const pulseButtonRef = useRef(null);
  const btnProps = {
    count,
    hasNew,
    modifiedCount,
    pulseButtonRef,
    tabIndex
  };

  const focusPulseButton = e => {
    if (e.key === 'Escape' && document.querySelector('.is-open .pulse-card-provider')) {
      // Pulse is a special case where the focus jumps to the body when Pulse popover is closed
      // Because there is no way to stop making it jump to the body at the moment,
      // we need to wait until after the focus is stable to then force focus to the Pulse button.
      setTimeout(() => pulseButtonRef?.current?.focus(), 50);
    }
  };
  useEffect(() => document.addEventListener('keydown', focusPulseButton), []);
  useEffect(() => () => document.removeEventListener('keydown', focusPulseButton), []);

  if (!hasAccess || !pulse || !pulse.timelineResponse) {
    return <PulseButton className="no-access" {...btnProps} />;
  }

  return (
    <Suspense fallback={<PulseButton className="wait" {...btnProps} />}>
      <PulseBoundary fallback={<PulseButton className="error" {...btnProps} />}>
        <PulseContainer
          hasNewAnnouncements={hasNew}
          key="notifications-badge"
          newNotificationsCount={modifiedCount}
          onUnreadChange={onPulseCountChanged}
          opened={opened}
          theme={theme}
          {...pulse}>
          <ShellOverlayTrigger
            animateFrom="top"
            closeOverlaysOnMenuShow={closeOverlaysOnMenuShow}
            element="pulse"
            full
            onHide={() => setOpened(false)}
            onShow={() => setOpened(true)}
            placement="bottom"
            trigger="click">
            {PulseButton({...btnProps})}
            <PulsePopover customButton={pulse.customButton} environment={environment} theme={theme} />
          </ShellOverlayTrigger>
        </PulseContainer>
      </PulseBoundary>
    </Suspense>
  );
}
